import { t } from "@travily/i18n";
import { EmptyState } from "@ui/components/EmptyState";
import { Heading } from "@ui/components/Heading";
import { Illustration } from "@ui/components/Illustration";
import { OutlineButton } from "@ui/components/OutlineButton";
import { ExperienceCard } from "./ExperienceCard";

export interface ExperienceEntry {
  dates: string;
  description: string;
  image: string;
  location: string;
  role: string;
  school: string;
}

export interface ExperienceProps {
  canCreate?: boolean;
  individualDisplayName: string;
  experiences: ExperienceEntry[];
}

export function Experience({ canCreate, individualDisplayName, experiences }: ExperienceProps) {
  return (
    <div className="mt-8 flex w-full flex-col gap-5 text-left lg:mt-12 lg:gap-6">
      <Heading.Root divider="responsive">
        <Heading.Title>{t("components.views.individuals.experience.title")}</Heading.Title>
        {canCreate ? (
          <Heading.Actions>
            <OutlineButton className="md:hidden" size="36">
              {t("components.views.individuals.experience.add")}
            </OutlineButton>
            <OutlineButton className="hidden md:inline-flex lg:hidden" size="36">
              {t("components.views.individuals.experience.add_experience")}
            </OutlineButton>
            <OutlineButton className="hidden lg:inline-flex">
              {t("components.views.individuals.experience.add_experience")}
            </OutlineButton>
          </Heading.Actions>
        ) : null}
      </Heading.Root>
      {experiences.length > 0 ? (
        <>
          <div className="flex flex-col">
            {experiences.map((experience, index) => (
              <div
                key={experience.school}
                className={index > 0 ? "border-t border-neutral-100 pt-5 lg:pt-6" : undefined}
              >
                <div className={index < experiences.length - 1 ? "pb-5 lg:pb-6" : undefined}>
                  <ExperienceCard {...experience} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex md:justify-center">
            <OutlineButton className="w-full md:hidden" size="36">
              {t("common.actions.view_more")}
            </OutlineButton>
            <OutlineButton className="hidden md:inline-flex lg:hidden" size="36">
              {t("common.actions.view_more")}
            </OutlineButton>
            <OutlineButton className="hidden lg:inline-flex">{t("common.actions.view_more")}</OutlineButton>
          </div>
        </>
      ) : (
        <EmptyState
          description={
            canCreate
              ? t("components.views.individuals.experience.empty_owner")
              : t("components.views.individuals.experience.empty_visitor", { name: individualDisplayName })
          }
          illustration={<Illustration name="experience" />}
          title={t("components.views.individuals.experience.empty_title")}
        />
      )}
    </div>
  );
}
